import React, { FC, useCallback, useState, useMemo, useEffect, useRef } from "react";
import AppContainer from "app_components/layouts/app";
import BreadcrumbComponent from 'app_components/commons/breadcrumb';
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import TinyEditorComponent from "app_components/commons/tinyEditor";
import LandingService from 'app_services/admin/landings/landingService';
import { ScreenNotificationsUtil } from "app_utils/screenNotification";
import './TermsAndConditionsLanding.scss';

const LANDING_SLUG = 'terms-and-conditions'

interface TermsBlock {
  title: string;
  content: string;
}

interface TermsBlocks {
  [key: string]: TermsBlock;
}

const TermsAndConditionsLanding: FC = () => {
  const { t } = useTranslation()
  const formHook = useForm();
  const landingService = useMemo(() => new LandingService(), []);
  const screenNotification = useMemo(() => new ScreenNotificationsUtil(), []);

  const [loadingForm, setLoadingForm] = useState<string>('')
  const [landingInfo, setLandingInfo] = useState<any>(null);
  const [tinyEditors, setTinyEditors] = useState<{[key: string]: string}>({});
  const [resetTinyEditors, setResetTinyEditors] = useState<{[key: string]: boolean}>({});

  // Referencia para controlar si el componente está montado
  const isMounted = useRef(true);

  const availableBlocks = useMemo(() => [
    { key: 'payment', title: 'Términos y condiciones para pagos' },
    // { key: 'exampleTwo', title: 'Otra sección' }
  ], []);

  // Inicializar los estados de los editores TinyMCE
  useEffect(() => {
    const initialTinyState: {[key: string]: string} = {};
    const initialResetState: {[key: string]: boolean} = {};

    availableBlocks.forEach(block => {
      initialTinyState[block.key] = '';
      initialResetState[block.key] = false;
    });

    setTinyEditors(initialTinyState);
    setResetTinyEditors(initialResetState);

    return () => {
      isMounted.current = false;
    };
  }, [availableBlocks]);

  const resetTinyEditor = useCallback((blockKey: string) => {
    setResetTinyEditors(prev => ({
      ...prev,
      [blockKey]: true
    }));

    setTimeout(() => {
      if (isMounted.current) {
        setResetTinyEditors(prev => ({
          ...prev,
          [blockKey]: false
        }));
      }
    }, 100);
  }, []);

  const getLandingData = useCallback(async () => {
    setLoadingForm('loading')
    try {
      const landingResponse = await landingService.fetchLandingData({
        slug: LANDING_SLUG
      });

      if (landingResponse && landingResponse.landing) {
        setLandingInfo(landingResponse);

        // Preparar los datos del formulario
        const formData: any = {};

        // Si existen términos y condiciones, cargar los datos
        if (landingResponse?.landing?.termsAndConditions) {
          // Actualizar los editores TinyMCE con los contenidos existentes
          const newTinyState: {[key: string]: string} = {...tinyEditors};

          // Para cada bloque disponible, verificar si hay datos
          availableBlocks.forEach(block => {
            const blockData = landingResponse.landing.termsAndConditions?.[block.key];

            if (blockData) {
              // Actualizar el estado del editor
              newTinyState[block.key] = blockData.content || '';

              // Preparar datos para el formulario
              if (!formData[block.key]) formData[block.key] = {};
              formData[block.key].title = blockData.title || '';
              formData[block.key].content = blockData.content || '';
            } else {
              // Inicializar con valores vacíos si no hay datos
              newTinyState[block.key] = '';
              if (!formData[block.key]) formData[block.key] = {};
              formData[block.key].title = '';
              formData[block.key].content = '';
            }
          });

          // Actualizar los estados de los editores
          setTinyEditors(newTinyState);

          // Resetear los editores para que carguen el nuevo contenido
          availableBlocks.forEach(block => {
            resetTinyEditor(block.key);
          });
        }

        // Resetear el formulario con los valores obtenidos
        formHook.reset(formData);
      }
    } catch (error) {
      console.error('Error fetching landing data:', error);
      screenNotification.fire({
        message: t('errors.something_went_wrong')
      });
    } finally {
      setLoadingForm('');
    }
  }, [landingService])

  useEffect(() => {
    getLandingData()
  }, [getLandingData])

  // Método para manejar cambios en el contenido de TinyMCE
  const handleTinyChange = useCallback((content: string, blockKey: string) => {
    setTinyEditors(prev => ({
      ...prev,
      [blockKey]: content
    }));
  }, []);


  // Método para manejar el envío del formulario
  const handleSubmit = async (formData: any) => {
    setLoadingForm('loading');

    try {
      const termsAndConditions: TermsBlocks = {};

      // Para cada bloque, combinar los datos del formulario con el contenido del editor
      availableBlocks.forEach(block => {
        if (formData[block.key]) {
          termsAndConditions[block.key] = {
            title: formData[block.key].title || '',
            content: tinyEditors[block.key] || ''
          };
        }
      });

      const data = {
        slug: LANDING_SLUG,
        termsAndConditions: termsAndConditions
      };

      const response = await landingService.saveTermsAndConditions(data);

      if (response) {
        screenNotification.fire({
          status: 'success',
          message: 'Actualización exitosa'
        });

        // Actualizar los datos después de guardar
        getLandingData();
      }
    } catch (error) {
      console.error('Error saving article:', error);
      screenNotification.fire({
        status: 'error',
        message: t('errors.something_went_wrong')
      });
    } finally {
      setLoadingForm('');
    }
  };

  return (
    <AppContainer>
      <div className='terms-and-conditions-landing-admin--layout'>
        <BreadcrumbComponent
          links={['/admin/landings', '#']}
          texts={[`${t('components.breadcrumb.landings')}`, `${t('components.breadcrumb.terms_and_conditions')}`]}
        />

        <div className="tutorials-landing-admin--content">
          <form onSubmit={formHook.handleSubmit(handleSubmit)} className="form-article mb-3">
            {availableBlocks.map((block) => (
              <>
                <div className="d-flex align-items-center mb-2">
                  <h3 className="form-title">{block.title}</h3>
                </div>
                <div className="col-12">
                  <div className="form-group">
                    <label htmlFor={`${block.key}[title]`}>{'Titulo'}</label>
                    {formHook.errors?.[block.key]?.title &&
                      <div className='invalid-feedback'>{t('errors.this_field_is_required')}</div>
                    }
                    <input
                      id={`${block.key}[title]`}
                      name={`${block.key}[title]`}
                      className={`form-control ${formHook.errors?.[block.key]?.title && 'is-invalid'}`}
                      type='text'
                      ref={formHook.register({ required: true })}
                      defaultValue={landingInfo?.landing?.termsAndConditions?.[block.key]?.title || ''}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-group">
                    <label>{'Contenido'}</label>
                    {!resetTinyEditors[block.key] && (
                      <TinyEditorComponent
                        data={tinyEditors[block.key] || ''}
                        onChange={(content) => handleTinyChange(content, block.key)}
                      />
                    )}
                  </div>
                </div>
              </>
            )
            )}
            <div className="col-12">
              <button
                className='btn btn-primary button-with-spinner'
                type='submit'
                disabled={loadingForm === 'loading'}
              >
                {loadingForm === 'loading' && (
                  <>
                    <div className="spinner-border spinner-loading-button" role="status"></div>&nbsp;&nbsp;
                  </>
                )}
                {t('buttons.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppContainer>
  );
};

export default TermsAndConditionsLanding
